/**
 * Google Analytics 4 Tracking Service
 * Simple GA4 implementation using gtag.js
 */

// Google Analytics 4 Measurement ID
const GA4_MEASUREMENT_ID = "G-3BVS8BRPJX";

// Google Analytics 4 global interface
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Initialize Google Analytics 4
const initializeAnalytics = () => {
  if (typeof window === 'undefined') return;

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID);
};

// Initialize analytics immediately
initializeAnalytics();

/**
 * Send event to Google Analytics 4
 */
const logEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track page views
 */
export const trackPageView = (pageName: string, pageUrl: string) => {
  logEvent('page_view', {
    page_title: pageName,
    page_location: pageUrl,
    page_path: pageUrl,
  });
};

/**
 * Track address searches
 */
export const trackAddressSearch = (address: string, suburb?: string, state?: string) => {
  logEvent('search', {
    search_term: address,
    suburb: suburb || 'unknown',
    state: state || 'unknown',
    search_type: 'property_address',
  });
};

/**
 * Track successful report generation
 */
export const trackReportGenerated = (address: string, suburb?: string, state?: string) => {
  logEvent('report_generated', {
    property_address: address,
    suburb: suburb || 'unknown',
    state: state || 'unknown',
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName: string, location: string) => {
  logEvent('button_click', {
    button_name: buttonName,
    button_location: location,
  });
};

/**
 * Track autocomplete selections
 */
export const trackAutocompleteSelection = (selectedAddress: string, position: number) => {
  logEvent('autocomplete_select', {
    selected_address: selectedAddress,
    position_in_list: position,
  });
};

/**
 * Track sample report views
 */
export const trackSampleReportView = (reportName: string) => {
  logEvent('sample_report_view', {
    report_name: reportName,
  });
};

/**
 * Track blog page views (list page)
 */
export const trackBlogListView = (postsCount: number) => {
  logEvent('blog_list_view', {
    posts_displayed: postsCount,
    page_type: 'blog_listing',
  });
};

/**
 * Track individual blog post views
 */
export const trackBlogPostView = (blogData: {
  postId: string;
  title: string;
  date: string;
  keywords: string[];
  sourcesCount: number;
}) => {
  logEvent('blog_post_view', {
    post_id: blogData.postId,
    post_title: blogData.title,
    post_date: blogData.date,
    keywords: blogData.keywords.slice(0, 5).join(', '), // First 5 keywords
    sources_count: blogData.sourcesCount,
    page_type: 'blog_post',
  });
};

/**
 * Track blog card clicks (from list to detail page)
 */
export const trackBlogCardClick = (blogData: {
  postId: string;
  title: string;
  position: number;
}) => {
  logEvent('blog_card_click', {
    post_id: blogData.postId,
    post_title: blogData.title,
    card_position: blogData.position,
    click_type: 'blog_navigation',
  });
};

/**
 * Track blog keyword tag clicks
 */
export const trackBlogKeywordClick = (keyword: string, postId: string) => {
  logEvent('blog_keyword_click', {
    keyword: keyword,
    post_id: postId,
    interaction_type: 'keyword_filter',
  });
};

/**
 * Track blog source link clicks
 */
export const trackBlogSourceClick = (sourceData: {
  postId: string;
  sourceName: string;
  sourceUrl: string;
  position: number;
}) => {
  logEvent('blog_source_click', {
    post_id: sourceData.postId,
    source_name: sourceData.sourceName,
    source_url: sourceData.sourceUrl,
    source_position: sourceData.position,
    click_type: 'external_link',
  });
};

/**
 * Track blog post reading time (when user leaves or scrolls significantly)
 */
export const trackBlogReadingTime = (postId: string, timeSpentSeconds: number) => {
  logEvent('blog_reading_time', {
    post_id: postId,
    time_spent_seconds: timeSpentSeconds,
    engagement_type: 'time_on_page',
  });
};

/**
 * Track blog post scroll depth
 */
export const trackBlogScrollDepth = (postId: string, scrollPercentage: number) => {
  logEvent('blog_scroll_depth', {
    post_id: postId,
    scroll_percentage: scrollPercentage,
    engagement_type: 'content_consumption',
  });
};

/**
 * Track blog pagination
 */
export const trackBlogPagination = (pageNumber: number, totalPages: number) => {
  logEvent('blog_pagination', {
    page_number: pageNumber,
    total_pages: totalPages,
    navigation_type: 'pagination',
  });
};

/**
 * Track blog image views
 */
export const trackBlogImageView = (postId: string, imageUrl: string) => {
  logEvent('blog_image_view', {
    post_id: postId,
    image_url: imageUrl,
    content_type: 'featured_image',
  });
};

